import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Property } from '@/types/schema';
import { X, Facebook, Twitter, Mail, Link as LinkIcon } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const ShareModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [property, setProperty] = useState<Property | null>(null);
  const [email, setEmail] = useState('');
  const [copying, setCopying] = useState(false);
  const { toast } = useToast();
  
  // Generate share links
  const shareUrl = property ? `${window.location.origin}/immobilie/${property.id}` : '';
  const shareTitle = property ? `${property.Title} | YADE Home GmbH` : '';
  const shareText = property ? `Luxusimmobilie in ${property.city}: ${property.Title}` : '';
  
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
  const whatsappShareUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + '\n' + shareUrl)}`;
  const emailShareUrl = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`;
  
  useEffect(() => {
    const handleShareProperty = (event: Event) => {
      const customEvent = event as CustomEvent<{ property: Property }>;
      setProperty(customEvent.detail.property);
      setOpen(true);
    };
    
    document.addEventListener('share:property', handleShareProperty);
    
    return () => {
      document.removeEventListener('share:property', handleShareProperty);
    };
  }, []);
  
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareUrl).then(() => {
      setCopying(true);
      setTimeout(() => setCopying(false), 2000);
      
      toast({
        title: "Link kopiert",
        description: "Der Link wurde in die Zwischenablage kopiert.",
      });
    });
  };
  
  const handleSendEmail = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "E-Mail-Adresse erforderlich",
        description: "Bitte geben Sie eine E-Mail-Adresse ein.",
        variant: "destructive",
      });
      return;
    }
    
    // In a real application, this would send an API request
    toast({
      title: "E-Mail gesendet",
      description: `Link zur Immobilie wurde an ${email} gesendet.`,
    });
    
    setEmail('');
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Immobilie teilen</DialogTitle>
          <DialogDescription>
            Teilen Sie diese Immobilie mit Freunden, Familie oder Geschäftspartnern.
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          <div className="mb-4">
            <label className="text-sm font-medium mb-2 block">Link kopieren</label>
            <div className="flex">
              <Input
                value={shareUrl}
                readOnly
                className="flex-grow rounded-r-none focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              <Button
                className="rounded-l-none"
                onClick={handleCopyLink}
              >
                {copying ? "Kopiert!" : "Kopieren"}
              </Button>
            </div>
          </div>
          
          <div className="mb-4">
            <label className="text-sm font-medium mb-2 block">Teilen über</label>
            <div className="grid grid-cols-4 gap-4 mt-2">
              <a
                href={facebookShareUrl}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center justify-center p-3 border border-gray-300 rounded-sm hover:border-global-cyan hover:text-global-cyan transition-colors"
              >
                <Facebook className="h-6 w-6 mb-1" />
                <span className="text-xs">Facebook</span>
              </a>
              
              <a
                href={twitterShareUrl}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center justify-center p-3 border border-gray-300 rounded-sm hover:border-global-cyan hover:text-global-cyan transition-colors"
              >
                <Twitter className="h-6 w-6 mb-1" />
                <span className="text-xs">Twitter</span>
              </a>
              
              <a
                href={whatsappShareUrl}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center justify-center p-3 border border-gray-300 rounded-sm hover:border-global-cyan hover:text-global-cyan transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mb-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                </svg>
                <span className="text-xs">WhatsApp</span>
              </a>
              
              <a
                href={emailShareUrl}
                target="_blank"
                rel="noreferrer"
                className="flex flex-col items-center justify-center p-3 border border-gray-300 rounded-sm hover:border-global-cyan hover:text-global-cyan transition-colors"
              >
                <Mail className="h-6 w-6 mb-1" />
                <span className="text-xs">E-Mail</span>
              </a>
            </div>
          </div>
          
          <form onSubmit={handleSendEmail}>
            <label className="text-sm font-medium mb-2 block">Per E-Mail senden</label>
            <div className="flex">
              <Input
                type="email"
                placeholder="E-Mail-Adresse eingeben"
                className="flex-grow rounded-r-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Button type="submit" className="rounded-l-none bg-black hover:bg-gray-800">
                Senden
              </Button>
            </div>
          </form>
        </div>
        
        <DialogFooter className="sm:justify-start">
          <Button type="button" variant="secondary" onClick={() => setOpen(false)}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
